import { StepConfigItem } from '~/Interfaces';
import { CountryCode, FlowTypes } from '~/enums';
import { ApplicationOverview } from './ApplicationOverview/ApplicationOverview';
import BankConnection from './BankConnection/BankConnectionStep';
import { CardOptIn } from './CardOptIn/CardOptIn';
import CompanySelector from './CompanySelector/CompanySelector';
import ConnectToEskat from './ConnectToEskat/ConnectToEskat';
import FinancialInformation from './FinancialInformation/FinancialInformation';
import LoanPurpose from './LoanPurpose/LoanPurpose';
import { AboutYou } from './AboutYou/AboutYou';
import { BusinessNeeds } from './BusinessNeeds/BusinessNeeds';
import { PersonalInformation } from './PersonalInformation/PersonalInformation';
import SelectGuarantor from './SelectGuarantor/SelectGuarantor';
import { StartApplication } from './StartApplication/StartApplication';
import { StartApplicationNew } from './StartApplicationNew/StartApplicationNew';
import { UploadDocuments } from './UploadDocuments/UploadDocuments';
import { Checkpoint } from './Checkpoint/Checkpoint';
import { LegalQuestions } from './LegalQuestions/LegalQuestions';

export const StartApplicationStep: StepConfigItem = {
  component: StartApplication,
  name: 'start_application_step',
};

export const CompanySelectorStep: StepConfigItem = {
  component: CompanySelector,
  name: 'company_selector_step',
};

export const PersonalInformationStep: StepConfigItem = {
  component: PersonalInformation,
  name: 'personal_information_step',
};

const LoanPurposeStep: StepConfigItem = {
  component: LoanPurpose,
  name: 'loan_purpose_step',
};

// 3-step basic flow steps A and B reuse the existing
// 'start_application_step' / 'personal_information_step' names (with new
// components behind them) rather than introducing new step names — they
// occupy the same position in the flow and existing step-name-keyed logic
// (useStepConfig's basic-steps case group, Cypress step names) then applies
// to them unchanged. Only step C is genuinely new.
export const StartApplicationNewStep: StepConfigItem = {
  component: StartApplicationNew,
  name: 'start_application_step',
};

export const AboutYouStep: StepConfigItem = {
  component: AboutYou,
  name: 'personal_information_step',
};

export const BusinessNeedsStep: StepConfigItem = {
  component: BusinessNeeds,
  name: 'business_needs_step',
};

const CardOptInStep: StepConfigItem = {
  component: CardOptIn,
  name: 'card_opt_in_step',
  hideNavbar: true,
  hideInProgressStepper: true,
};

const CheckpointStep: StepConfigItem = {
  component: Checkpoint,
  name: 'checkpoint_step',
  hideInProgressStepper: true,
  hideNavbar: true,
};

const ApplicationOverviewStep: StepConfigItem = {
  component: ApplicationOverview,
  name: 'application_overview_step',
  hideInProgressStepper: true,
};

const SelectGuarantorStep: StepConfigItem = {
  component: SelectGuarantor,
  name: 'guarantor_selector_step',
};

const BankConnectionStep: StepConfigItem = {
  component: BankConnection,
  name: 'connect_to_bank_step',
};

const FinancialInformationStep: StepConfigItem = {
  component: FinancialInformation,
  name: 'financial_information_step',
};

const UploadDocumentsStep: StepConfigItem = {
  component: UploadDocuments,
  name: 'upload_documents_step',
};

const EskatStep: StepConfigItem = {
  component: ConnectToEskat,
  name: 'connect_to_eskat_step',
};

const LegalQuestionsStep: StepConfigItem = {
  component: LegalQuestions,
  name: 'legal_questions_step',
};

export const stepConfigPerMarketFlowType: Record<
  CountryCode,
  Record<FlowTypes, StepConfigItem[]>
> = {
  [CountryCode.SE]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.FI]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.NO]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.DK]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      EskatStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      EskatStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.BE]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      LegalQuestionsStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      LegalQuestionsStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
    ],
  },
  [CountryCode.NL]: {
    [FlowTypes.Authenticated]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
    [FlowTypes.Manual]: [
      StartApplicationStep,
      PersonalInformationStep,
      CardOptInStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
  },
  [CountryCode.DE]: {
    [FlowTypes.Authenticated]: [
      CompanySelectorStep,
      LoanPurposeStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      FinancialInformationStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
    [FlowTypes.Manual]: [
      CompanySelectorStep,
      LoanPurposeStep,
      PersonalInformationStep,
      CheckpointStep,
      ApplicationOverviewStep,
      SelectGuarantorStep,
      FinancialInformationStep,
      BankConnectionStep,
      UploadDocumentsStep,
    ],
  },
};

// 3-step variant of the basic flow (behind
// AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL, currently
// tested in NL only — see market gating in useStepConfig.ts): contact
// details are collected first, so a lead is recoverable by Marketing even
// if the applicant drops off before finishing the rest of the basic flow
// (OB-5217 / OB-5345). Selected in useStepConfig.ts instead of
// stepConfigPerMarketFlowType[market] when the flag variant is 'test'.
const ThreeStepBasicFlow: StepConfigItem[] = [
  StartApplicationNewStep,
  AboutYouStep,
  BusinessNeedsStep,
  CardOptInStep,
  CheckpointStep,
  ApplicationOverviewStep,
  SelectGuarantorStep,
  BankConnectionStep,
  UploadDocumentsStep,
];

export const threeStepBasicFlowConfigPerFlowType: Record<
  FlowTypes,
  StepConfigItem[]
> = {
  [FlowTypes.Authenticated]: ThreeStepBasicFlow,
  [FlowTypes.Manual]: ThreeStepBasicFlow,
};
